import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { routePaths } from 'router/route-paths.ts';
import { useCreateCargo } from 'api/cargos/use-create-cargo.ts';
import type { CreateCargoReq } from 'api/swagger';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import { formatDate } from 'shared/utils/time/format-date';
import { CargoType } from 'types/cargo';
import type { ICargoFormFields } from 'components/cargos/cargo-form';
import { CargoForm } from 'components/cargos/cargo-form';
import { prepareCargoAddressPayload, prepareOptionalCargoAddressPayload } from 'components/cargos/cargo-form/utils';
import { EFormDrawerSize, FormDrawerWithButton } from 'components/common/form-drawer-with-button';

interface ICargoCreateProps {
  orderId: number;
}

export const CargoCreateComponent: React.FC<ICargoCreateProps> = (props) => {
  const { orderId } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: createCargo, isLoading } = useCreateCargo();

  const handleFinish = async (values: ICargoFormFields) => {
    const isContainerCargoEnabled = values.type === CargoType.CONTAINER;
    const loadingAddress = prepareCargoAddressPayload(values.loadingAddress);
    const unloadingAddress = prepareCargoAddressPayload(values.unloadingAddress);

    const apiVariables: CreateCargoReq = {
      orderId,
      loadingDateFrom: values.loadingDate?.[0] && formatDate(values.loadingDate?.[0]),
      loadingDateTo: values.loadingDate?.[1] && formatDate(values.loadingDate?.[1]),
      unloadingDateFrom: values.unloadingDate?.[0] && formatDate(values.unloadingDate?.[0]),
      unloadingDateTo: values.unloadingDate?.[1] && formatDate(values.unloadingDate?.[1]),
      loadingCustom: values.loadingCustom,
      loadingCustomAddress: prepareOptionalCargoAddressPayload(values.loadingCustomAddress),
      unloadingCustom: values.unloadingCustom,
      unloadingCustomAddress: prepareOptionalCargoAddressPayload(values.unloadingCustomAddress),
      assetReturnAddress:
        values.type === CargoType.CONTAINER ? prepareOptionalCargoAddressPayload(values.assetReturnAddress) : undefined,
      type: values.type,
      loadingAddress: {
        ...loadingAddress,
        extraData: loadingAddress.extraData && loadingAddress.extraData?.length ? loadingAddress.extraData : null,
      },
      unloadingAddress: {
        ...unloadingAddress,
        extraData: unloadingAddress.extraData && unloadingAddress.extraData?.length ? unloadingAddress.extraData : null,
      },
      totalWeight: values.totalWeight,
      cargoItems: isContainerCargoEnabled ? undefined : values.cargoItems || [],
      specificCargoItem: isContainerCargoEnabled ? values.specificCargoItem : undefined,
    };
    const cargo = await createCargo(apiVariables);

    navigate(generatePath(routePaths.cargo, { cargoId: String(cargo.id) }));
  };

  return (
    <FormDrawerWithButton
      formComponent={CargoForm}
      title={t('cargo:create_cargo')}
      buttonLabel={t('cargo:add_cargo')}
      isLoading={isLoading}
      size={EFormDrawerSize.LARGE}
      displayMaskClickConfirmation
      onFinish={handleFinish}
    />
  );
};

export const CargoCreate = withRoleGuard<ICargoCreateProps>(CargoCreateComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.customer_manager,
]);
